import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './CompanyStructure.css';
import { LockedCreateButton, useCanBuild } from '../../components/common/ModuleInterest';

/* The company's org chart: its units, who leads each one, and who sits where.

   Three screens on one route family:
     /company-structure            the whole tree, one row per unit
     /company-structure/:unitId    one unit — its head, the units under it, the people in it
     /company-structure/unplaced   the people who sit in no unit yet

   ★★ No culture numbers here. Who sits where and who leads — never how a unit is doing. Those
   figures live on the heatmap behind two anonymity floors, and this screen lists people BY NAME;
   putting a score next to a list of names is the one thing it must never do.

   ★ The head does not have to sit in the unit. The CEO heads the group but is placed in 'Board',
   and the unit page says so ("sits in …") rather than pretending the two are the same.

   ★ Moving somebody moves what they do from now on. Answers already given stay with the unit they
   were given in (they carry a snapshot), and the screen says that next to the button. */

const KIND_KEY = {
  group: 'osKindGroup',
  entity: 'osKindEntity',
  division: 'osKindDivision',
  department: 'osKindDepartment',
  team: 'osKindTeam',
};

const Avatar = ({ person }) => (person && person.photo_url ? (
  <img className="os-avatar" src={person.photo_url} alt="" />
) : (
  <span className="os-avatar os-avatar--empty">
    {(((person || {}).name || '?').trim().charAt(0)) || '?'}
  </span>
));

const Kind = ({ kind }) => {
  const { t } = useTranslation();
  return KIND_KEY[kind] ? <span className="os-kind">{t(KIND_KEY[kind])}</span> : null;
};

/* The head as one line: face + name, or the honest absence. A head who has left the company is
   shown as gone rather than as a name nobody can find on the roster any more. */
const HeadLine = ({ head }) => {
  const { t } = useTranslation();
  if (!head) return <span className="os-muted">{t('osNoHead')}</span>;
  if (head.gone) return <span className="os-muted">{t('osHeadGone')}</span>;
  return (
    <span className="os-head">
      <Avatar person={head} />
      <span>{head.name}</span>
    </span>
  );
};

/* The "move to / place in" picker: the tree as an indented list, so a reader sees WHERE a
   department sits, not just its name — 'Nursing' under two hospitals is two different places. */
const UnitSelect = ({ options, value, onChange, exclude, placeholder }) => (
  <select
    className="form-select form-select-sm os-select"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">{placeholder}</option>
    {options.filter((o) => o.id !== exclude).map((o) => (
      <option key={o.id} value={o.id}>
        {`${'  '.repeat(o.depth)}${o.name}`}
      </option>
    ))}
  </select>
);

const Coverage = ({ totals }) => {
  const { t } = useTranslation();
  // The paid "Own events" module behind every build button here (null while it loads).
  const canBuild = useCanBuild();
  if (!totals) return null;
  return (
    <div className="os-coverage">
      <span>
        <strong>{totals.placed}</strong>{' '}
        {t('osCoverage', { people: totals.people, units: totals.units })}
      </span>
      {totals.unplaced > 0 && (canBuild === false ? (
        /* Placing people in the structure is the paid "Own events" module. */
        <LockedCreateButton label={t('osSeeUnplaced', { n: totals.unplaced })} />
      ) : (
        <Link to="/company-structure/unplaced" className="os-coverage__warn">
          {t('osSeeUnplaced', { n: totals.unplaced })}
        </Link>
      ))}
    </div>
  );
};

/* ---- the whole tree ------------------------------------------------------------------------ */

const Overview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiRequest('GET', '/company/structure')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  const units = (data && data.units) || [];
  return (
    <>
      <Back to="/member-home" />
      <Title my="1" className="mt-3">
        <Icon name="sitemap" theme="primary" className="me-2" />
        {t('osTitle')}
      </Title>
      <p className="text-muted text-start os-intro">{t('osIntro')}</p>

      {!data && <p className="text-muted">{t('loading')}</p>}
      {data && <Coverage totals={data.totals} />}

      {data && units.length === 0 && (
        <div className="os-note">
          <strong>{t('osEmptyTitle')}</strong>
          <span>{t('osEmptyBody')}</span>
        </div>
      )}

      {units.length > 0 && (
        <div className="os-scroll">
          <table className="os-table">
            <thead>
              <tr>
                <th>{t('osUnit')}</th>
                <th>{t('osHead')}</th>
                <th className="os-num">{t('osPeople')}</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u) => (
                <tr key={u.id}>
                  <td>
                    <span className="os-unit" style={{ paddingLeft: `${u.depth * 16}px` }}>
                      <Link to={`/company-structure/${u.id}`}>{u.name}</Link>
                      <Kind kind={u.kind} />
                    </span>
                  </td>
                  <td><HeadLine head={u.head} /></td>
                  <td className="os-num">{u.headcount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {units.length > 0 && (
        <p className="os-foot">
          <Link to="/company-heatmap">
            <Icon name="chart-tree-map" className="me-1" />
            {t('osToHeatmap')}
          </Link>
        </p>
      )}
    </>
  );
};

/* ---- choosing a head ------------------------------------------------------------------------ */

/* People of this unit first — the usual answer — then a search over the whole roster, because the
   head can sit anywhere. A search and not a list: a hospital group is tens of thousands of names. */
const HeadPicker = ({ unitId, local, current, onDone, onCancel }) => {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setHits([]); return undefined; }
    timer.current = setTimeout(() => {
      apiRequest('GET', `/company/structure/people?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setHits(r.people || []))
        .catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const choose = async (personId) => {
    setBusy(true);
    try {
      await apiRequest('POST', `/company/structure/unit/${unitId}/head`, { person_id: personId });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  const list = q.trim().length >= 2 ? hits : local;
  return (
    <div className="os-picker">
      <input
        className="form-control form-control-sm"
        placeholder={t('osSearchPeople')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      {q.trim().length < 2 && local.length > 0 && (
        <div className="os-picker__label">{t('osPeopleHere')}</div>
      )}
      <div className="os-picker__list">
        {list.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`os-picker__item${current && current.id === p.id ? ' is-on' : ''}`}
            disabled={busy}
            onClick={() => choose(p.id)}
          >
            <Avatar person={p} />
            <span>{p.name}</span>
          </button>
        ))}
        {q.trim().length >= 2 && hits.length === 0 && (
          <span className="os-muted">{t('osNoMatch')}</span>
        )}
      </div>
      <div className="os-picker__actions">
        {current && (
          <button type="button" className="btn btn-sm btn-link os-danger" disabled={busy}
                  onClick={() => choose(null)}>
            {t('osClearHead')}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-link" onClick={onCancel}>
          {t('cancel')}
        </button>
      </div>
    </div>
  );
};

/* ---- one person, with a "move" action -------------------------------------------------------- */

const PersonRow = ({ person, options, currentUnit, onMoved, actionLabel }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await apiRequest('POST', '/company/structure/place', { person_id: person.id, unit_id: target });
      setOpen(false);
      setTarget('');
      onMoved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="os-person">
      <div className="os-person__row">
        <Avatar person={person} />
        <span className="os-person__name">{person.name}</span>
        {person.is_head && (
          <span className="os-badge"><Icon name="crown" />{t('osHeadBadge')}</span>
        )}
        {!open && (
          <button type="button" className="btn btn-sm btn-link os-person__act"
                  onClick={() => setOpen(true)}>
            {actionLabel}
          </button>
        )}
      </div>
      {open && (
        <div className="os-person__move">
          <UnitSelect options={options} value={target} onChange={setTarget}
                      exclude={currentUnit} placeholder={t('osChooseUnit')} />
          <button type="button" className="btn btn-sm btn-primary" disabled={!target || busy}
                  onClick={save}>
            {t('save')}
          </button>
          <button type="button" className="btn btn-sm btn-link" onClick={() => setOpen(false)}>
            {t('cancel')}
          </button>
          <span className="os-hint">{t('osMoveHint')}</span>
        </div>
      )}
    </li>
  );
};

/* ---- one unit ------------------------------------------------------------------------------- */

const UnitPage = ({ unitId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [picking, setPicking] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', `/company/structure/unit/${unitId}`)
      .then(setData)
      // A unit that is gone (retired, or a stale link) lands on the whole tree, never on a blank.
      .catch(() => navigate('/company-structure', { replace: true }));
  }, [unitId, navigate]);

  useEffect(() => { setData(null); setPicking(false); load(); }, [load]);

  if (!data) {
    return (<><Back to="/company-structure" /><p className="text-muted mt-3">{t('loading')}</p></>);
  }
  const { unit, head, children, people, trail, options } = data;
  const parent = trail.length ? trail[trail.length - 1] : null;

  return (
    <>
      <Back to={parent ? `/company-structure/${parent.id}` : '/company-structure'} />
      <nav className="os-trail" aria-label={t('osTitle')}>
        <Link to="/company-structure">{t('osAll')}</Link>
        {trail.map((c) => (
          <React.Fragment key={c.id}>
            <span aria-hidden="true">›</span>
            <Link to={`/company-structure/${c.id}`}>{c.name}</Link>
          </React.Fragment>
        ))}
      </nav>
      <Title my="1" className="mt-2">
        {unit.name} <Kind kind={unit.kind} />
      </Title>
      <p className="text-muted text-start os-intro">
        {t('osUnitCounts', { total: unit.headcount, direct: unit.direct })}
      </p>

      <section className="os-card">
        <div className="os-card__label"><Icon name="user-crown" className="me-1" />{t('osHead')}</div>
        {!picking && (
          <div className="os-headcard">
            {head && !head.gone ? (
              <>
                <Avatar person={head} />
                <div className="os-headcard__body">
                  <strong>{head.name}</strong>
                  {head.unit && (
                    <span className="os-muted">
                      {t('osSitsIn')}{' '}
                      <Link to={`/company-structure/${head.unit.id}`}>{head.unit.name}</Link>
                    </span>
                  )}
                  {head.placed === false && (
                    <span className="os-muted">{t('osHeadUnplaced')}</span>
                  )}
                </div>
              </>
            ) : (
              <span className="os-muted">{head && head.gone ? t('osHeadGone') : t('osNoHead')}</span>
            )}
            <button type="button" className="btn btn-sm btn-outline-primary os-headcard__btn"
                    onClick={() => setPicking(true)}>
              {head ? t('osChangeHead') : t('osSetHead')}
            </button>
          </div>
        )}
        {picking && (
          <HeadPicker
            unitId={unit.id}
            local={people}
            current={head && !head.gone ? head : null}
            onCancel={() => setPicking(false)}
            onDone={() => { setPicking(false); load(); }}
          />
        )}
      </section>

      {children.length > 0 && (
        <section className="os-card">
          <div className="os-card__label">{t('osSubUnits')}</div>
          <ul className="os-list">
            {children.map((c) => (
              <li key={c.id}>
                <Link to={`/company-structure/${c.id}`} className="os-sub">
                  <span className="os-sub__name">{c.name} <Kind kind={c.kind} /></span>
                  <HeadLine head={c.head} />
                  <span className="os-num">{c.headcount}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="os-card">
        <div className="os-card__label">{t('osPeopleIn', { name: unit.name })}</div>
        {people.length === 0 ? (
          <span className="os-muted">
            {children.length ? t('osNobodyDirectSub') : t('osNobodyDirect')}
          </span>
        ) : (
          <ul className="os-list">
            {people.map((p) => (
              <PersonRow key={p.id} person={p} options={options} currentUnit={unit.id}
                         onMoved={load} actionLabel={t('osMove')} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
};

/* ---- nobody's unit yet ------------------------------------------------------------------------ */

const Unplaced = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/company/structure/unplaced')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const people = (data && data.people) || [];
  return (
    <>
      <Back to="/company-structure" />
      <Title my="1" className="mt-3">{t('osUnplacedTitle')}</Title>
      <p className="text-muted text-start os-intro">{t('osUnplacedIntro')}</p>
      {!data && <p className="text-muted">{t('loading')}</p>}
      {data && people.length === 0 && (
        <div className="os-note"><strong>{t('osAllPlaced')}</strong></div>
      )}
      {people.length > 0 && (
        <ul className="os-list">
          {people.map((p) => (
            <PersonRow key={p.id} person={p} options={data.options} currentUnit={null}
                       onMoved={load} actionLabel={t('osPlace')} />
          ))}
        </ul>
      )}
    </>
  );
};

const CompanyStructure = () => {
  const { unitId } = useParams();
  let body = <Overview />;
  if (unitId === 'unplaced') body = <Unplaced />;
  else if (unitId) body = <UnitPage unitId={unitId} />;
  return (
    <Base activeNav="home">
      <Card>{body}</Card>
    </Base>
  );
};

export default CompanyStructure;
